import React, { useCallback, useRef, useState } from "react";
import useCapture from "./useCapture";
import { GROUP_LABEL, colorFor, hrefFor } from "./palette";
import KindPicker from "./KindPicker";
import SetupModal from "./SetupModal";
import { PER_BRANCH_CHOICES } from "./useAtlas";

const NODE_R = 7;
const OPEN_R = 11;

// Pan and zoom, by hand. Wheel zooms about the pointer, drag moves the map,
// and the buttons exist because a trackpad is not the only thing people have.
function useViewport(frame) {
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const drag = useRef(null);

  const onPointerDown = (event) => {
    if (event.target.closest("[data-atlas-hit]")) return;
    drag.current = { x: event.clientX, y: event.clientY, view };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event) => {
    if (!drag.current) return;
    setView({
      ...drag.current.view,
      x: drag.current.view.x + (event.clientX - drag.current.x),
      y: drag.current.view.y + (event.clientY - drag.current.y)
    });
  };

  const onPointerUp = () => { drag.current = null; };

  const onWheel = (event) => {
    if (!event.ctrlKey && Math.abs(event.deltaY) < 1) return;
    event.preventDefault();
    setView((current) => {
      const k = Math.min(3, Math.max(0.35, current.k * (event.deltaY < 0 ? 1.12 : 0.89)));
      return { ...current, k };
    });
  };

  const zoom = (factor) =>
    setView((current) => ({ ...current, k: Math.min(3, Math.max(0.35, current.k * factor)) }));
  const fit = () => setView({ x: 0, y: 0, k: 1 });

  return { fit, frame, onPointerDown, onPointerMove, onPointerUp, onWheel, view, zoom };
}

export default function AtlasMap({
  atlas, onSelectEdge, onSelectNode, selected
}) {
  const {
    frame, nodesByKey, openKeys, origin, positions, visibleEdges, visibleKeys
  } = atlas;
  const viewport = useViewport(frame);
  const svgRef = useRef(null);
  const capture = useCapture(svgRef);
  const [hovered, setHovered] = useState(null);
  const [settingUp, setSettingUp] = useState(false);

  // The modal asks the server rather than searching the payload: the payload
  // carries the shape of the web and not much else, and a search wants the
  // degree and the label too.
  const search = useCallback(async (query) => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    const response = await fetch(`/correspondences/search?${params}`, {
      headers: { Accept: "application/json" }
    });
    if (!response.ok) throw new Error("the search could not be reached");
    return response.json();
  }, []);

  const applySetup = useCallback(({ kinds, perBranch, startKey }) => {
    if (kinds) atlas.setKinds(kinds);
    if (perBranch !== undefined) atlas.setPerBranch(perBranch);
    if (startKey) {
      atlas.goTo(startKey);
      const node = nodesByKey.get(startKey);
      if (node) onSelectNode(node);
    }
    setSettingUp(false);
  }, [atlas, nodesByKey, onSelectNode]);

  // Hovering a node quiets everything it is not connected to. Musicmap's
  // trick, and the reason a dense web stays readable.
  const lit = useCallback((edge) => {
    if (!hovered) return true;
    return edge.from === hovered || edge.to === hovered;
  }, [hovered]);

  const nodeLit = useCallback((key) => {
    if (!hovered) return true;
    if (key === hovered) return true;
    return visibleEdges.some((edge) =>
      (edge.from === hovered && edge.to === key) || (edge.to === hovered && edge.from === key));
  }, [hovered, visibleEdges]);

  const groupsShown = Array.from(
    new Set(visibleKeys.map((key) => nodesByKey.get(key)?.group).filter(Boolean))
  );

  return (
    <div className="atlas" data-testid="atlas">
      <SetupModal
        kindCounts={atlas.kindCounts}
        kinds={atlas.kinds}
        onApply={applySetup}
        onClose={() => setSettingUp(false)}
        open={settingUp}
        perBranch={atlas.perBranch}
        perBranchChoices={PER_BRANCH_CHOICES}
        search={search}
      />

      <div className="atlas__toolbar">
        <div className="atlas__toolgroup">
          <span className="atlas__toolgroup-label">Start and move</span>
          <div className="atlas__toolgroup-controls">
            <button
              type="button"
              className="atlas__setup"
              data-testid="atlas-setup"
              onClick={() => setSettingUp(true)}
            >
              Choose a starting point
            </button>
            <div className="atlas__zoom">
              <button type="button" onClick={() => viewport.zoom(1.2)} aria-label="Zoom in">+</button>
              <button type="button" onClick={() => viewport.zoom(0.83)} aria-label="Zoom out">−</button>
              <button type="button" onClick={viewport.fit} data-testid="atlas-fit">Recentre</button>
            </div>
          </div>
        </div>
        <div className="atlas__toolgroup">
          <span className="atlas__toolgroup-label">Limit what appears</span>
          <div className="atlas__toolgroup-controls">
            <KindPicker
              counts={atlas.kindCounts}
              onChange={atlas.setKinds}
              selected={atlas.kinds}
            />

            {/* How much of a thing to draw at once. Venus has fifty-seven
                correspondences and most of them are one of Agrippa's plants. */}
            <label className="atlas__per-branch">
              Connections per thing
              <select
                value={atlas.perBranch === null ? "all" : String(atlas.perBranch)}
                data-testid="atlas-per-branch"
                onChange={(event) => atlas.setPerBranch(
                  event.target.value === "all" ? null : Number(event.target.value)
                )}
              >
                {PER_BRANCH_CHOICES.map((choice) => (
                  <option key={String(choice)} value={choice === null ? "all" : String(choice)}>
                    {choice === null ? "every connection" : `first ${choice}`}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div className="atlas__toolgroup">
          <span className="atlas__toolgroup-label">Choose what to follow</span>
          {/* "all" is the default: a dense web is sometimes the point. */}
          <div className="viewtoggle atlas__show" role="group" aria-label="How much of the web to draw">
            {[
              ["all", "Everything"],
              ["selected", "Selection"],
              ["chain", "Build a chain"]
            ].map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                className={`viewtoggle__tab${atlas.showMode === mode ? " viewtoggle__tab--on" : ""}`}
                aria-pressed={atlas.showMode === mode}
                data-testid={`atlas-show-${mode}`}
                onClick={() => atlas.setShowMode(mode)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="atlas__toolgroup">
          <span className="atlas__toolgroup-label">More actions</span>
          <div className="atlas__actions">
            <button
              type="button"
              className={`atlas__capture${capture.arming ? " atlas__capture--armed" : ""}`}
              aria-pressed={capture.arming}
              data-testid="atlas-capture"
              onClick={capture.arm}
            >
              {capture.arming ? "Drag a box, or press to cancel" : "Copy image"}
            </button>
            <button type="button" onClick={atlas.openAll} data-testid="atlas-open-all">
              Open the full map
            </button>
            <button
              type="button"
              onClick={atlas.reset}
              disabled={openKeys.size <= 1}
              data-testid="atlas-reset"
            >
              Back to {nodesByKey.get(origin)?.name || "the start"}
            </button>
          </div>
        </div>

        <div className="atlas__legend">
          <span className="atlas__toolgroup-label">Kinds now visible</span>
          <ul className="atlas__key" aria-label="What the colours mean">
            {groupsShown.map((group) => (
              <li key={group}>
                <span className="atlas__swatch" style={{ background: colorFor(group) }} aria-hidden="true" />
                {GROUP_LABEL[group] || group}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {atlas.showMode === "chain" ? (
        <div className="chainbar" data-testid="atlas-chainbar">
          {atlas.chain.length === 0 ? (
            <p className="chainbar__empty">
              Pick things on the map and the shortest way between them is drawn.
              Every link is an edge somebody cited; the joining of them is yours.
            </p>
          ) : (
            <>
              <ol className="chainbar__list">
                {atlas.chain.map((key, index) => (
                  <li key={`${key}-${index}`}>
                    <span>{nodesByKey.get(key)?.name || key}</span>
                    <button
                      type="button"
                      className="chainbar__drop"
                      aria-label={`Take ${nodesByKey.get(key)?.name || key} out of the chain`}
                      data-testid={`chain-drop-${key}`}
                      onClick={() => atlas.dropFromChain(index)}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ol>
              <p className="chainbar__reading" data-testid="atlas-chain-reading">
                {atlas.chainResult.steps.length === 0
                  ? "One more, and the way between them is drawn."
                  : atlas.chainResult.steps
                      .map((step) => `${nodesByKey.get(step.from)?.name} — ${step.edge.label} — ${nodesByKey.get(step.to)?.name}`)
                      .join(" · ")}
              </p>
              {/* A gap is an ANSWER. A chain drawn with a hole in it,
                  pretending otherwise, is not. */}
              {atlas.chainResult.gaps.map(([from, to]) => (
                <p key={`${from}-${to}`} className="chainbar__gap" data-testid="atlas-chain-gap">
                  Nothing in this web joins {nodesByKey.get(from)?.name} to {nodesByKey.get(to)?.name}.
                </p>
              ))}
              <button
                type="button"
                className="chainbar__clear"
                data-testid="atlas-chain-clear"
                onClick={atlas.clearChain}
              >
                Start a new chain
              </button>
            </>
          )}
        </div>
      ) : null}

      <div
        className={`atlas__canvas${capture.arming ? " atlas__canvas--arming" : ""}`}
        onPointerDown={(event) => {
          // While a region is being chosen the drag belongs to the marquee,
          // not to panning.
          if (capture.onPointerDown(event)) return;
          viewport.onPointerDown(event);
        }}
        onPointerMove={(event) => {
          if (capture.onPointerMove(event)) return;
          viewport.onPointerMove(event);
        }}
        onPointerUp={(event) => {
          if (capture.onPointerUp(event)) return;
          viewport.onPointerUp(event);
        }}
        onPointerCancel={viewport.onPointerUp}
        onWheel={viewport.onWheel}
      >
        <svg
          ref={svgRef}
          className="atlas__svg"
          viewBox={`${frame.x} ${frame.y} ${frame.width} ${frame.height}`}
          role="group"
          aria-label="A map of correspondences. Every name here is also in the list below."
          data-testid="atlas-svg"
        >
          <g transform={`translate(${viewport.view.x} ${viewport.view.y}) scale(${viewport.view.k})`}>
            {visibleEdges.map((edge) => {
              const a = positions.get(edge.from);
              const b = positions.get(edge.to);
              if (!a || !b) return null;
              const from = nodesByKey.get(edge.from)?.name;
              const to = nodesByKey.get(edge.to)?.name;
              return (
                <g
                  key={edge.id}
                  className="atlas__edge-group"
                  data-atlas-hit="edge"
                  onClick={() => onSelectEdge(edge)}
                >
                  <title>{`${from} — ${edge.label} — ${to}`}</title>
                  <line
                    className="atlas__edge"
                    /* Solid where an ancient source was found, dashed where the
                       search was made and came back empty. The thesis of the
                       whole section, in the drawing. */
                    data-sacred={edge.sacred}
                    data-status={edge.status}
                    data-selected={selected?.type === "edge" && selected.id === edge.id}
                    data-lit={lit(edge)}
                    x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                    strokeWidth={Math.min(1 + (edge.sourceCount * 0.55), 4)}
                  />
                  {/* A one-pixel line is not a target. Same answer as the
                      Tree's paths: a transparent stroke wide enough to hit,
                      carrying the pointer for the drawn line beneath it. */}
                  <line
                    className="atlas__edge-hit"
                    data-testid={`atlas-edge-${edge.id}`}
                    x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                  />
                </g>
              );
            })}

            {visibleKeys.map((key) => {
              const node = nodesByKey.get(key);
              const point = positions.get(key);
              if (!node || !point) return null;
              const isOpen = openKeys.has(key);
              const isSelected = selected?.type === "node" && selected.key === key;
              const held = isOpen ? atlas.hiddenAround(key) : 0;

              return (
                <g
                  key={key}
                  className="atlas__node"
                  data-testid={`atlas-node-${key}`}
                  data-open={isOpen}
                  data-selected={isSelected}
                  data-lit={nodeLit(key)}
                  data-atlas-hit="node"
                  /* Picked up by the delegated hover card, which fetches the
                     thing's own sentence. Half a second here: pointing at a
                     node is deliberate in a way that crossing a table is not. */
                  data-correspondence-key={key}
                  data-hover-delay="500"
                  transform={`translate(${point.x} ${point.y})`}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  aria-label={`${node.name}, ${node.kind}. ${isOpen ? "Open" : "Closed"}. Press to ${isOpen ? "read it" : "open its connections"}.`}
                  onPointerEnter={() => setHovered(key)}
                  onPointerLeave={() => setHovered(null)}
                  onFocus={() => setHovered(key)}
                  onBlur={() => setHovered(null)}
                  onClick={() => {
                    if (atlas.showMode === "chain") atlas.addToChain(key);
                    atlas.open(key);
                    onSelectNode(node);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    if (atlas.showMode === "chain") atlas.addToChain(key);
                    atlas.open(key);
                    onSelectNode(node);
                  }}
                >
                  <circle
                    className="atlas__halo"
                    r={isOpen ? OPEN_R + 7 : NODE_R + 7}
                    fill={colorFor(node.group)}
                  />
                  <circle
                    className="atlas__dot"
                    r={isOpen ? OPEN_R : NODE_R}
                    fill={colorFor(node.group)}
                  />
                  <text className="atlas__label" x={0} y={(isOpen ? OPEN_R : NODE_R) + 15} textAnchor="middle">
                    {node.name}
                  </text>
                  {/* What this thing is holding back, and how to see it. */}
                  {held > 0 ? (
                    <text
                      className="atlas__held"
                      data-testid={`atlas-held-${key}`}
                      x={0}
                      y={(isOpen ? OPEN_R : NODE_R) + 28}
                      textAnchor="middle"
                      onClick={(event) => { event.stopPropagation(); atlas.expand(key); }}
                    >
                      +{held} more
                    </text>
                  ) : null}
                </g>
              );
            })}
          </g>
        </svg>

        {capture.marquee ? (
          <div
            className="atlas__marquee"
            data-testid="atlas-marquee"
            style={{
              left: capture.marquee.left,
              top: capture.marquee.top,
              width: capture.marquee.width,
              height: capture.marquee.height
            }}
          />
        ) : null}

        {capture.status ? (
          <p
            className={`atlas__capture-status atlas__capture-status--${capture.status.ok ? "ok" : "no"}`}
            role="status"
            data-testid="atlas-capture-status"
          >
            {capture.status.message}
          </p>
        ) : null}

        {atlas.unopened > 0 ? (
          <p className="atlas__more" data-testid="atlas-unopened">
            {atlas.unopened} more {atlas.unopened === 1 ? "thing" : "things"} out there. Open a circle to go further.
          </p>
        ) : null}
      </div>
    </div>
  );
}
